import { faPhone } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { OutboundDestination } from "@phone/outboundCallsClient";
import {
  readPreferredLine,
  writePreferredLine,
} from "@phone/outboundLinePreference";
import { formatPhoneNumber, type OutboundLine } from "@phone/phoneSetupClient";
import { enableSoftphone, useSoftphoneStore } from "@phone/softphone";
import { useOutboundCall } from "@phone/useOutboundCall";
import { useOutboundLines } from "@phone/useOutboundLines";
import {
  Button,
  type ButtonProps,
  cn,
  Dropdown,
  type DropdownItem,
  FieldError,
} from "@scribenote/brian-design-system";
import { useOpenCalls } from "@shell/navigation";
import { useState } from "react";
import { createPortal } from "react-dom";

const SETTINGS_ITEM = "headset-settings";
const CONNECT_ITEM = "headset-connect";

const lineName = (line: OutboundLine): string =>
  line.kind === "browser" ? "This computer" : line.label;

/**
 * With more than one line the click asks which; the last pick is marked but
 * never dialed unasked. An unregistered browser is offered as "connect and
 * call", never dialed directly: it would ring out to nothing. Under
 * `outbound_computer_default` the headset rings unasked unless it can't register.
 */
export const CallButton = ({
  destination,
  missingReason = "No number to call",
  label = "Call",
  variant = "outline",
  size = "sm",
  buttonClassName,
  feedbackContainer,
  testId,
  compact = false,
  onAdmitted,
}: {
  destination: OutboundDestination | null;
  missingReason?: string;
  label?: string;
  variant?: ButtonProps["variant"];
  size?: ButtonProps["size"];
  buttonClassName?: string;
  feedbackContainer?: HTMLElement | null;
  testId: string;
  /** Fine print rather than a full error line. */
  compact?: boolean;
  /** A host that opened this in a dialog closes it here. */
  onAdmitted?: () => void;
}) => {
  const offered = useOutboundLines();
  const headsetStatus = useSoftphoneStore((s) => s.status);
  const headsetOwnership = useSoftphoneStore((s) => s.ownership);
  const endpointId = useSoftphoneStore((s) => s.endpointId);
  const headsetReady = headsetStatus === "ready";
  const headsetOwner = headsetOwnership !== "non-owner";
  const {
    attempt,
    start,
    retry: resume,
    reset,
  } = useOutboundCall({ onAdmitted });
  const [refused, setRefused] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);
  /** The clinic's browser default failed to connect: the picker takes over. */
  const [headsetDeclined, setHeadsetDeclined] = useState(false);
  const [lastDialed, setLastDialed] = useState<OutboundLine | null>(null);
  const openCalls = useOpenCalls();

  // brian returns a browser row per paired installation; offer one "This
  // computer", preferring this registration's own row.
  const browserLine =
    offered.status === "ready"
      ? (offered.lines.find(
          (line) => line.kind === "browser" && line.id === endpointId,
        ) ?? offered.lines.find((line) => line.kind === "browser"))
      : undefined;
  const lines =
    offered.status === "ready"
      ? offered.lines.filter((line) =>
          line.kind === "browser" ? headsetReady && line === browserLine : true,
        )
      : [];
  const canConnect = browserLine !== undefined && !headsetReady && headsetOwner;
  const choices = lines.length + (canConnect ? 1 : 0);
  const browserByDefault =
    offered.status === "ready" &&
    offered.outboundComputerDefault &&
    browserLine !== undefined &&
    (headsetReady || canConnect) &&
    !headsetDeclined;
  const preferred = readPreferredLine();
  const busy =
    connecting || attempt.phase === "dialing" || attempt.phase === "checking";
  const disabled = destination === null || offered.status === "loading";

  /** A browser line dials this registration's `endpointId`: the listed row may be another installation's. */
  const dial = (line: OutboundLine) => {
    if (!destination) return;
    writePreferredLine(line.id);
    setRefused(null);
    setLastDialed(line);
    const endpointId =
      line.kind === "browser"
        ? (useSoftphoneStore.getState().endpointId ?? line.id)
        : line.id;
    start(endpointId, destination, { browser: line.kind === "browser" });
  };

  const connectAndDial = async () => {
    if (!browserLine) return;
    setConnecting(true);
    await enableSoftphone();
    setConnecting(false);
    const after = useSoftphoneStore.getState();
    if (after.status === "ready") dial(browserLine);
    else {
      setRefused(after.error ?? "Brian couldn't connect the headset.");
      setHeadsetDeclined(true);
    }
  };

  const clickAlone = () => {
    reset();
    setRefused(null);
    if (offered.status === "loading") return;
    if (offered.status === "unavailable") {
      setRefused(offered.message);
      return;
    }
    if (browserByDefault) {
      if (headsetReady) dial(browserLine);
      else void connectAndDial();
      return;
    }
    if (lines.length === 1) {
      dial(lines[0]);
      return;
    }
    if (canConnect) {
      void connectAndDial();
      return;
    }
    setRefused("No lines are set up for Brian to ring.");
  };

  const items: DropdownItem[] = [
    ...(canConnect
      ? [
          {
            id: CONNECT_ITEM,
            label: "This computer",
            trailing: (
              <span className="font-mono text-fine-print text-annotation">
                {headsetStatus === "connecting"
                  ? "connecting…"
                  : "connect headset"}
              </span>
            ),
          } satisfies DropdownItem,
        ]
      : []),
    ...lines.map<DropdownItem>((line, index) => ({
      id: line.id,
      label: lineName(line),
      trailing: (
        <span className="font-mono text-fine-print text-annotation">
          {line.kind === "browser"
            ? "Headset"
            : formatPhoneNumber(line.addressE164)}
          {line.id === preferred && " · last time"}
        </span>
      ),
      dividerAfter: index === lines.length - 1,
    })),
    { id: SETTINGS_ITEM, label: "Headset settings…" },
  ];

  const note =
    refused ??
    (attempt.phase === "refused"
      ? attempt.message
      : attempt.phase === "checking"
        ? "Checking the call…"
        : connecting
          ? "Connecting the headset…"
          : attempt.phase === "dialing"
            ? "Calling…"
            : null);
  const failed = refused !== null || attempt.phase === "refused";
  const retryable = attempt.phase === "refused" && attempt.retryable;

  const face = (
    <>
      <FontAwesomeIcon icon={faPhone} aria-hidden="true" />
      {label}
    </>
  );

  /** Not `clickAlone`: with several options it falls through to "no lines are set up". */
  const retryDial = () => {
    reset();
    setRefused(null);
    if (resume()) return;
    if (lastDialed) dial(lastDialed);
    else clickAlone();
  };

  const retry = retryable && (
    <>
      {" "}
      <button
        type="button"
        className="cursor-pointer underline decoration-dotted"
        onClick={retryDial}
        data-testid={`${testId}-retry`}
      >
        Try again
      </button>
    </>
  );

  const feedback = (
    <>
      {destination === null && missingReason && (
        <span
          className="text-fine-print text-annotation"
          data-testid={`${testId}-missing`}
        >
          {missingReason}
        </span>
      )}

      {note &&
        (failed && !compact ? (
          <FieldError data-testid={`${testId}-note`}>
            {note}
            {retry}
          </FieldError>
        ) : (
          <span
            role="status"
            className={cn(
              "text-fine-print",
              failed ? "text-ink-stopped" : "text-annotation",
            )}
            data-testid={`${testId}-note`}
          >
            {note}
            {retry}
          </span>
        ))}
    </>
  );

  return (
    <span className="inline-flex flex-col items-start gap-1">
      {choices > 1 && !browserByDefault ? (
        <Dropdown
          items={items}
          placement="bottom-start"
          className="w-64"
          onSelect={(id) => {
            if (id === SETTINGS_ITEM) {
              openCalls();
              return;
            }
            if (id === CONNECT_ITEM) {
              void connectAndDial();
              return;
            }
            const line = lines.find((entry) => entry.id === id);
            if (line) dial(line);
          }}
          trigger={(props) => (
            <Button
              {...props}
              onClick={() => {
                reset();
                setRefused(null);
                props.onClick();
              }}
              className={buttonClassName}
              variant={variant}
              size={size}
              busy={busy}
              disabled={disabled}
              title={destination === null ? missingReason : undefined}
              data-testid={testId}
            >
              {face}
            </Button>
          )}
        />
      ) : (
        <Button
          className={buttonClassName}
          variant={variant}
          size={size}
          busy={busy}
          disabled={disabled}
          title={destination === null ? missingReason : undefined}
          onClick={clickAlone}
          data-testid={testId}
        >
          {face}
        </Button>
      )}

      {feedbackContainer ? createPortal(feedback, feedbackContainer) : feedback}
    </span>
  );
};
